/**
 * DriverFirstLogin
 *
 * Mandatory password-change screen that the driver hits the first time they
 * log in with an admin-issued temp password. DriverRoute redirects every
 * other path here while `mustChangePassword` is true, so this is the
 * inescapable gate before the dashboard.
 *
 * On success we update the cached session so `mustChangePassword=false`
 * locally, then send the driver to their dashboard.
 */
import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight, FiCheck } from 'react-icons/fi';
import { dChangePassword, disAuthenticated, dauthenticate } from '../auth/helper/driverIndex';
import AuthLayout from '../core/auth/AuthLayout';
import AuthField from '../core/auth/AuthField';

const brand = {
  eyebrow: 'Driver app',
  title: 'Welcome aboard.',
  text: 'Pick a password only you know. You will use it with your username every time you sign in.',
};

export default function DriverFirstLogin() {
  const session = disAuthenticated();
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword]   = useState('');
  const [confirm,    setConfirm]        = useState('');
  const [busy,       setBusy]           = useState(false);
  const [err,        setErr]            = useState(null);
  const [done,       setDone]           = useState(false);

  if (!session) return <Redirect to="/driverSignin" />;
  const home = session.driver?.appVersion === 'v1' ? '/driver/dashboard' : '/driver/app';
  // If the gate has already been cleared (e.g. visiting this page directly),
  // forward to the dashboard rather than getting stuck in a loop.
  if (!session.driver?.mustChangePassword && !done) return <Redirect to={home} />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!tempPassword)         return setErr('Enter the temporary password your dispatcher gave you.');
    if (newPassword.length < 6) return setErr('New password must be at least 6 characters.');
    if (newPassword !== confirm) return setErr('Passwords don\'t match.');

    setBusy(true);
    try {
      const driverId = session.driver?._id;
      const token    = session.token;
      const res = await dChangePassword({ password: tempPassword, newPassword }, driverId, token);
      if (res?.error) { setErr(res.error); return; }
      // Persist the cleared flag in the local session so DriverRoute lets us through.
      const updated = { ...session, driver: { ...session.driver, mustChangePassword: false } };
      dauthenticate(updated, () => {});
      setDone(true);
      // Tiny delay so the success copy is visible before the redirect.
      setTimeout(() => { window.location.replace(home); }, 600);
    } catch {
      setErr('Network error. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout bannerRole="driver" brand={brand} back={null}>
      <h2 className="ta-title">Hi {session.driver?.firstName || 'there'}, set your password</h2>
      <p className="ta-sub">
        Enter the temporary password your dispatcher gave you, then choose a new one.
      </p>

      <form onSubmit={submit} noValidate>
        <div className="ta-fields">
          <AuthField
            id="first-temp"
            label="Temporary password"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            value={tempPassword}
            onChange={(e) => setTempPassword(e.target.value.trim())}
            autoFocus
          />
          <AuthField
            id="first-new"
            label="New password (6+ characters)"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <AuthField
            id="first-confirm"
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        {err && (
          <div className="ta-alert" role="alert">
            <FiAlertCircle size={18} aria-hidden="true" /><span>{err}</span>
          </div>
        )}
        {done && (
          <div className="ta-alert is-ok" role="status">
            <FiCheck size={18} aria-hidden="true" /><span>Password set. Taking you to your jobs…</span>
          </div>
        )}

        <button type="submit" className="ta-btn" disabled={busy || done}>
          {busy
            ? <><span className="ta-spinner" aria-hidden="true" /> Saving…</>
            : <>Set password &amp; continue <FiArrowRight size={18} aria-hidden="true" /></>}
        </button>
      </form>
    </AuthLayout>
  );
}
